import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEndUser } from "@/lib/endUserAuth";
import { endUserAPI } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { User, Users, LogOut, ShieldAlert } from "lucide-react";

export default function AppProfilePage() {
  const { endUser, relationships, logout } = useEndUser();
  const navigate = useNavigate();
  const [cancelling, setCancelling] = useState(false);

  const onLogout = () => {
    logout();
    navigate("/app/login", { replace: true });
  };

  // Fase 5e — derecho ARCO de cancelación (LFPDPPP).
  const onArcoCancel = async () => {
    setCancelling(true);
    try {
      const res = await endUserAPI.arcoCancel();
      const retained = res.data?.retained_clinical_sessions || 0;
      toast.success(
        retained > 0
          ? "Datos cancelados. Tus registros clínicos se conservan por ley (NOM-024)."
          : "Tus datos y tu cuenta fueron eliminados."
      );
      logout();
      navigate("/app/login", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.error || "No pudimos procesar la cancelación");
      setCancelling(false);
    }
  };

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-foreground">Perfil</h1>

      <Card className="shadow-sm">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <User className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate">{endUser?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{endUser?.email}</p>
          </div>
        </CardContent>
      </Card>

      {relationships.length > 0 && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />Profesionales vinculados
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {relationships.map((r) => (
              <div key={r.id} className="rounded-lg bg-muted/30 px-3 py-2">
                <p className="text-sm font-medium">{r.tenant?.name || "Clínica"}</p>
                <p className="text-xs text-muted-foreground">como {r.subject?.name}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Button variant="outline" className="w-full rounded-full" onClick={onLogout} data-testid="app-logout">
        <LogOut className="w-4 h-4 mr-1.5" />Cerrar sesión
      </Button>

      <Card className="shadow-sm border-destructive/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-destructive" />Privacidad
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Puedes ejercer tu derecho de cancelación (ARCO) en cualquier momento. Se
            eliminarán tus sesiones de entrenamiento y tu cuenta. Los registros de
            tratamiento clínico se conservan 5 años por obligación legal (NOM-024).
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                className="w-full rounded-full"
                disabled={cancelling}
                data-testid="app-arco-cancel"
              >
                Cancelar mis datos (ARCO)
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="max-w-sm rounded-xl">
              <AlertDialogHeader>
                <AlertDialogTitle>¿Cancelar tus datos?</AlertDialogTitle>
                <AlertDialogDescription>
                  Esta acción es irreversible: se eliminarán tus sesiones de
                  entrenamiento, se desvincularán tus profesionales y tu cuenta
                  dejará de existir. Si tienes historial clínico, esos registros se
                  conservarán de forma anónima el tiempo que exige la ley.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full">Conservar mis datos</AlertDialogCancel>
                <AlertDialogAction
                  className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={onArcoCancel}
                  data-testid="app-arco-confirm"
                >
                  Sí, eliminar todo
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
}
